import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { SEO_MAP } from "@/lib/seoMap";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    const dest = from || (res.user.role === "admin" ? "/admin" : "/portal");
    nav(dest, { replace: true });
  };

  return (
    <main className="min-h-[100svh] pt-32 pb-20 grid-bg relative overflow-hidden" data-testid="login-page">
      <SEO {...SEO_MAP.login} />
      <div className="hero-orb w-[500px] h-[500px] -top-40 -left-40 glow-primary" />
      <div className="relative max-w-[440px] mx-auto px-6">
        <div className="overline mb-4">Sign in</div>
        <h1 className="font-serif text-5xl tracking-[-0.02em] leading-tight">
          Welcome<br /><span className="italic">back</span>.
        </h1>
        <p className="mt-3 text-[14.5px] text-[color:var(--text-muted)]">
          Access your projects, invoices and admin CMS.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-4">
          <Field label="Email" value={email} onChange={setEmail} type="email" testId="login-email" />
          <Field label="Password" value={password} onChange={setPassword} type="password" testId="login-password" />
          {error && <div className="text-[13px] text-red-500" data-testid="login-error">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[color:var(--text)] text-[color:var(--bg)] font-semibold text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-50 hover:opacity-90 transition-opacity"
            data-testid="login-submit"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
          </button>
        </form>

        <div className="mt-8 text-[13px] text-[color:var(--text-muted)]">
          New client? <Link to="/register" className="link-underline text-[color:var(--text)]" data-testid="login-to-register">Create an account</Link>
        </div>

        <div className="mt-10 p-4 rounded-xl border border-[color:var(--border)] text-[12.5px] text-[color:var(--text-muted)] font-mono leading-relaxed">
          <div className="mb-1 uppercase tracking-widest text-[10.5px]">Demo credentials</div>
          Admin · admin@lanexlabs.com / LaneX2025Admin<br />
          Client · client@demo.com / Client2025Demo
        </div>

        <div className="mt-12 text-[13px] text-[color:var(--text-muted)] leading-relaxed space-y-3">
          <p>
            The LaneX Labs client portal gives you a single, secure home for every project we're delivering
            together. From your dashboard you can track progress across active engagements, view and pay
            invoices, raise support tickets against defined SLAs, exchange messages with your delivery team,
            and access shared files, domains, hosting and maintenance requests.
          </p>
          <p>
            Administrators use the same sign-in to access the LaneX Console — our internal CMS for managing
            services, portfolio projects, testimonials, FAQs, blog articles, industries, media library and
            incoming project leads. Access is scoped by role and protected end-to-end with encrypted sessions.
          </p>
          <p>
            Signing in is fast and secure. Passwords are hashed with bcrypt at rest, sessions are carried
            over HttpOnly SameSite cookies rather than tokens in local storage, and every authentication
            event is logged for audit. If you've forgotten your password, contact your account manager or
            email hello@lanexlabs.com and we'll issue a secure reset link. For enhanced security we can
            enable multi-factor authentication on request for enterprise accounts.
          </p>
          <p>
            Don't have an account yet? Create one in under a minute — or if you're a prospective client,
            please <a href="/contact" className="underline decoration-dotted">start with a project enquiry</a>{" "}
            and we'll provision your portal once we're in flight.
          </p>
        </div>
      </div>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 rounded-xl border border-[color:var(--border-strong)] bg-transparent px-4 text-[14.5px] focus:border-brand-primary transition-colors"
        required
        data-testid={testId}
      />
    </label>
  );
}
